import type { SnapshotLicenseUsage } from '@ipfabric/api-client/client/schemas';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import classNames from 'classnames';
import {
  formatLicenseUsageDetail,
  formatSnapshotLicenseUsage,
  toSnapshotLicenseUsageValues,
} from 'pages/SnapshotManagement/LicenseUsage/licenseUsageFormatter';
import type { FC, JSX } from 'react';
import { CLOUD_LICENSING_PAGE_PATH } from 'utils/pageGating/cloudLicensingPageGating';
import { useApiScopeRequirementMet } from 'utils/permissions/useApiScopeRequirementMet';

type LicenseUsageProps = {
  className?: string;
  licenseUsage: NonNullable<SnapshotLicenseUsage>;
};

const tierNames: {
  [key: string]: string;
} = {
  tierA: 'A-tier objects',
  tierB: 'B-tier objects',
  tierC: 'C-tier objects',
};

export const LicenseUsage: FC<LicenseUsageProps> = ({
  className,
  licenseUsage,
}) => {
  const apiScopeRequirementMet = useApiScopeRequirementMet();
  const licenseUsageValues = toSnapshotLicenseUsageValues(licenseUsage);
  const onPremDetail = formatLicenseUsageDetail(
    licenseUsageValues.onPremise,
    'devices',
  );
  const cloudDetail = formatLicenseUsageDetail(licenseUsageValues.cloud, 'CCU');
  const tierCcuCounts = licenseUsage.cloud.tierCcuCounts ?? [];

  return (
    <div className={classNames('flex items-center gap-1', className)}>
      {formatSnapshotLicenseUsage(licenseUsageValues)}
      <TooltipIcon
        icon='fa-question-circle'
        size='xs'
        tooltipProps={{
          placement: 'right',
          highlighted: true,
        }}
        interactive
        content={
          <div className='flex flex-col gap-3xs text-left items-start'>
            <span className='font-semibold text-sm'>
              Licence usage breakdown
            </span>
            {onPremDetail !== null && (
              <div className='flex gap-2.5 justify-between self-stretch'>
                <span>On-prem:</span>
                <span>{onPremDetail}</span>
              </div>
            )}
            {cloudDetail !== null && (
              <div className='flex gap-2.5 justify-between self-stretch'>
                <span>Cloud:</span>
                <div className='flex flex-col items-end'>
                  <span>{cloudDetail}</span>
                  {tierCcuCounts.reduce((acc, [name, count]) => {
                    // Count is expected to be string because of backend DB NUMERIC type
                    count &&
                      count !== '0' &&
                      acc.push(
                        <span key={name} className='text-plain text-xs'>
                          {`${count} for ${(typeof name === 'string' && tierNames[name]) || name}`}
                        </span>,
                      );

                    return acc;
                  }, [] as JSX.Element[])}
                </div>
              </div>
            )}
            {apiScopeRequirementMet({
              method: 'POST',
              path: '/tables/settings/administration/license-consumption/overview',
            }) ? (
              <Link
                to={CLOUD_LICENSING_PAGE_PATH}
                icon='fa-chevron-right'
                iconPosition='end'
              >
                Show usage detail
              </Link>
            ) : null}
          </div>
        }
      />
    </div>
  );
};
